import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = [
  { k: "mon", label: "Mo" }, { k: "tue", label: "Di" }, { k: "wed", label: "Mi" },
  { k: "thu", label: "Do" }, { k: "fri", label: "Fr" }, { k: "sat", label: "Sa" }, { k: "so", label: "So" },
];
// backend uses "sun" – fix mapping
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export const StaffTab = ({ pin }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6A7B62");
  const [selected, setSelected] = useState(null); // full staff obj being edited
  const [exceptDate, setExceptDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/staff/list`, { pin });
      setStaff(data);
      if (selected) setSelected(data.find((s) => s.id === selected.id) || null);
    } catch { toast.error("Konnte Team nicht laden"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Name fehlt");
    try {
      await axios.post(`${API}/admin/staff/create`, { pin, name: name.trim(), color, active: true });
      setName(""); setColor("#6A7B62"); toast.success("Mitarbeiter angelegt"); load();
    } catch { toast.error("Anlegen fehlgeschlagen"); }
  };

  const patch = async (id, updates) => {
    try { await axios.patch(`${API}/admin/staff/${id}`, { pin, ...updates }); load(); }
    catch { toast.error("Speichern fehlgeschlagen"); }
  };

  const del = async (id) => {
    if (!window.confirm("Mitarbeiter wirklich löschen?")) return;
    try { await axios.delete(`${API}/admin/staff/${id}?pin=${pin}`); toast.success("Gelöscht"); setSelected(null); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const updateSchedule = (dayKey, blocks) => {
    if (!selected) return;
    const next = { ...selected.weekly_schedule, [dayKey]: blocks };
    setSelected({ ...selected, weekly_schedule: next });
  };
  const addBlock = (dayKey) => updateSchedule(dayKey, [...(selected.weekly_schedule[dayKey] || []), { start: "09:00", end: "17:00" }]);
  const removeBlock = (dayKey, i) => updateSchedule(dayKey, selected.weekly_schedule[dayKey].filter((_, idx) => idx !== i));
  const setBlockField = (dayKey, i, field, val) => {
    const arr = [...selected.weekly_schedule[dayKey]];
    arr[i] = { ...arr[i], [field]: val };
    updateSchedule(dayKey, arr);
  };
  const saveSchedule = () => patch(selected.id, { weekly_schedule: selected.weekly_schedule, exceptions: selected.exceptions || [] });

  const addException = () => {
    if (!exceptDate) return toast.error("Datum wählen");
    const next = [...(selected.exceptions || []), { date: exceptDate, closed: true, note: "Frei" }];
    setSelected({ ...selected, exceptions: next });
    setExceptDate("");
  };
  const removeException = (i) => setSelected({ ...selected, exceptions: selected.exceptions.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="tab-staff" className="grid md:grid-cols-[320px_1fr] gap-8">
      {/* Liste */}
      <div>
        <h3 className="font-serif text-xl mb-4">Team</h3>
        <div className="border border-border divide-y divide-border">
          {staff.map((s) => (
            <button key={s.id} data-testid={`staff-item-${s.id}`}
              onClick={() => setSelected(s)}
              className={`w-full flex items-center justify-between p-3 text-left hover:bg-muted transition-colors ${selected?.id === s.id ? "bg-muted" : ""}`}>
              <span className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{ background: s.color }} />
                <span>{s.name}</span>
              </span>
              <span className={`text-[10px] uppercase tracking-widest ${s.active ? "text-primary" : "text-foreground/40"}`}>{s.active ? "aktiv" : "inaktiv"}</span>
            </button>
          ))}
          {!staff.length && !loading && <p className="p-4 text-sm text-muted-foreground">Noch kein Team.</p>}
        </div>

        <div className="mt-6 border border-border p-4 space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Neu anlegen</p>
          <Input data-testid="staff-new-name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="flex items-center gap-3 text-sm">
            <Palette size={16} /> <input data-testid="staff-new-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 border border-border" />
            <span className="text-muted-foreground text-xs">Farbe für Kalender</span>
          </label>
          <button data-testid="staff-add-btn" onClick={add} className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 text-sm">
            <Plus size={16} /> Mitarbeiter hinzufügen
          </button>
        </div>
      </div>

      {/* Detail: Wochenplan + Ausnahmen */}
      <div>
        {!selected ? (
          <div className="border border-dashed border-border p-10 text-center text-muted-foreground">Wähle links einen Mitarbeiter aus.</div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <input type="color" value={selected.color} onChange={(e) => setSelected({ ...selected, color: e.target.value })} className="w-8 h-8 border border-border" />
                <Input value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} className="max-w-xs" />
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={selected.active} onChange={(e) => setSelected({ ...selected, active: e.target.checked })} /> aktiv
                </label>
              </div>
              <div className="flex gap-2">
                <button data-testid="staff-save" onClick={() => patch(selected.id, { name: selected.name, color: selected.color, active: selected.active, weekly_schedule: selected.weekly_schedule, exceptions: selected.exceptions || [] })} className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm">
                  <Save size={14} /> Speichern
                </button>
                <button data-testid="staff-delete" onClick={() => del(selected.id)} className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-foreground/60 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Standard-Wochenplan</p>
              <div className="grid gap-2">
                {DAY_KEYS.map((k, di) => {
                  const blocks = selected.weekly_schedule?.[k] || [];
                  return (
                    <div key={k} className="flex items-start gap-3 py-2 border-b border-border">
                      <div className="w-10 pt-1 text-sm">{DAY_LABELS[di]}</div>
                      <div className="flex-1 space-y-2">
                        {blocks.length === 0 && <span className="text-xs text-foreground/40">Geschlossen</span>}
                        {blocks.map((b, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input type="time" value={b.start} onChange={(e) => setBlockField(k, i, "start", e.target.value)} className="border border-border px-2 py-1 text-sm" />
                            <span className="text-foreground/40">–</span>
                            <input type="time" value={b.end} onChange={(e) => setBlockField(k, i, "end", e.target.value)} className="border border-border px-2 py-1 text-sm" />
                            <button onClick={() => removeBlock(k, i)} className="text-foreground/40 hover:text-red-600 text-xs">entf.</button>
                          </div>
                        ))}
                        <button onClick={() => addBlock(k)} className="text-xs text-primary hover:underline">+ Zeitraum</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button data-testid="staff-schedule-save" onClick={saveSchedule} className="mt-4 flex items-center gap-2 border border-primary text-primary px-4 py-2 text-sm hover:bg-primary hover:text-primary-foreground transition-colors">
                <Save size={14} /> Wochenplan speichern
              </button>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Ausnahmetage (Urlaub, Fortbildung)</p>
              <div className="flex items-center gap-2 mb-3">
                <Input type="date" value={exceptDate} onChange={(e) => setExceptDate(e.target.value)} className="max-w-xs" />
                <button onClick={addException} className="border border-border px-3 py-2 text-sm">Tag frei nehmen</button>
              </div>
              <ul className="divide-y divide-border border border-border">
                {(selected.exceptions || []).map((e, i) => (
                  <li key={i} className="flex items-center justify-between p-2 text-sm">
                    <span>{e.date} — {e.closed ? "geschlossen" : "Sonderzeiten"} {e.note ? `· ${e.note}` : ""}</span>
                    <button onClick={() => removeException(i)} className="text-foreground/40 hover:text-red-600 text-xs">löschen</button>
                  </li>
                ))}
                {!(selected.exceptions || []).length && <li className="p-3 text-xs text-muted-foreground">Keine Ausnahmen.</li>}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
